import {
  MARKETING_FIELD_CATALOG,
  MARKETING_FIELD_GROUP_ORDER,
  type MarketingFieldGroup,
} from '@utazzo/shared';
import { UzButton, UzInput } from '@utazzo/ui';
import { Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';

import { applyWithPrerequisites } from '../prerequisites';
import { usePermissionRegistry } from '../use-roles';

import {
  useSetUserMarketingFieldVisibility,
  useSetUserPermissions,
  useUserMarketingFieldVisibility,
  useUserPermissions,
  useUsersList,
} from '@/features/users/use-users';

/**
 * Per-user permission overrides (ADR-0039 follow-up).
 *
 * Two-state additive model: each (user, module-action) cell is either
 * OFF, GRANTED-BY-ROLE (locked-on, no checkbox toggle), or GRANTED
 * (operator checked it as an extra). The matrix never DENIES - to
 * remove access from a single user, the operator edits the role
 * grant.
 *
 * Layout: left pane is a search-as-you-type user list; right pane is
 * the read/write matrix for the currently selected user. Saves are
 * replace-all so the operator sees the canonical state immediately
 * after click + the server filters out role-duplicate codes before
 * persisting.
 */
export const UserPermissionsPanel = (): React.JSX.Element => {
  const [search, setSearch] = useState('');
  const usersQuery = useUsersList({
    page: 1,
    pageSize: 50,
    sortDir: 'asc',
    ...(search.trim().length >= 2 ? { search: search.trim() } : {}),
  });
  const registry = usePermissionRegistry();

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const snapshot = useUserPermissions(selectedUserId);
  const save = useSetUserPermissions();

  // The `user` module (user.read / user.create / user.update /
  // user.deactivate) is admin-only by policy: granting user-management
  // permissions to a non-admin would be a privilege-escalation vector.
  // We use the selected user's role to lock the `user` module row in
  // the matrix - the operator can still grant other modules' perms to
  // anyone, but only an ADMIN target user can carry user.* extras.
  const selectedUserRole = useMemo(() => {
    const rows = usersQuery.data?.data ?? [];
    return rows.find((u) => u.id === selectedUserId)?.role ?? null;
  }, [usersQuery.data, selectedUserId]);
  const targetIsAdmin = selectedUserRole === 'ADMIN';

  // Auto-select the first user when the list arrives so the matrix is
  // never blank on first visit.
  useEffect(() => {
    if (selectedUserId) return;
    const first = usersQuery.data?.data[0];
    if (first) setSelectedUserId(first.id);
  }, [selectedUserId, usersQuery.data]);

  // Local pending set of codes the operator has checked. We hydrate it
  // from the server's `userPermissions` whenever the snapshot
  // changes (different user picked, or just-saved roundtrip).
  const [pendingExtras, setPendingExtras] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (snapshot.data) {
      setPendingExtras(new Set(snapshot.data.userPermissions));
    }
  }, [snapshot.data]);

  const roleCodes = useMemo(() => new Set(snapshot.data?.rolePermissions ?? []), [snapshot.data]);

  const dirty = useMemo(() => {
    const baseline = new Set(snapshot.data?.userPermissions ?? []);
    if (baseline.size !== pendingExtras.size) return true;
    for (const c of baseline) if (!pendingExtras.has(c)) return true;
    return false;
  }, [snapshot.data, pendingExtras]);

  const toggleCell = (code: string): void => {
    // Role-granted cells are locked on - no toggle.
    if (roleCodes.has(code)) return;
    // user.* module is admin-only - block the toggle when target user
    // is non-admin. The UI also renders these cells visually locked
    // (see PermissionMatrixGrid), but the guard here is the source of
    // truth so a stale render can't accidentally grant.
    if (code.startsWith('user.') && !targetIsAdmin) return;
    const next = new Set(pendingExtras);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    // A section / tab ticked brings its page access along (unless the role
    // already gives it); page access unticked takes its sections with it.
    setPendingExtras(
      applyWithPrerequisites(pendingExtras, next, roleCodes, registry.data?.modules ?? []),
    );
  };

  const handleSave = (): void => {
    if (!selectedUserId) return;
    save.mutate({ id: selectedUserId, codes: Array.from(pendingExtras) });
  };

  const handleReset = (): void => {
    if (snapshot.data) setPendingExtras(new Set(snapshot.data.userPermissions));
  };

  return (
    // Two columns, narrow on the left. The user panel sticks at
    // ~220-260px so it never crowds the matrix; matrix takes the rest
    // and scrolls its own table when the action set grows.
    // Below lg the panes stack (user picker on top, matrix beneath); the
    // desktop two-column split is unchanged.
    <div className="flex flex-1 flex-col overflow-y-auto lg:grid lg:grid-cols-[minmax(220px,260px)_1fr] lg:overflow-hidden">
      {/* ----- Left: user picker (compact rows, smaller hit area) ----- */}
      <aside className="flex max-h-64 min-h-0 flex-col border-b border-zinc-200 lg:max-h-none lg:border-r lg:border-b-0">
        <div className="border-b border-zinc-200 p-2">
          <UzInput
            name="user-perms-search"
            placeholder="Search users"
            leftAdornment={<Search className="h-3.5 w-3.5" />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="flex-1 overflow-y-auto p-1.5">
          {usersQuery.isLoading ? (
            <div className="p-2 text-xs text-zinc-500">Loading…</div>
          ) : (usersQuery.data?.data ?? []).length === 0 ? (
            <div className="p-2 text-xs text-zinc-500">No users match.</div>
          ) : (
            <ul className="flex flex-col gap-0.5">
              {(usersQuery.data?.data ?? []).map((u) => {
                const active = u.id === selectedUserId;
                return (
                  <li key={u.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedUserId(u.id)}
                      className={
                        'flex w-full flex-col gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors ' +
                        (active ? 'bg-zinc-100 ring-1 ring-zinc-300' : 'hover:bg-zinc-50')
                      }
                    >
                      <span className="truncate text-[13px] font-medium text-zinc-900">
                        {u.fullName}
                      </span>
                      <span className="truncate text-[11px] text-zinc-500">{u.email}</span>
                      <span className="text-[9px] tracking-wide text-zinc-400 uppercase">
                        {u.role}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </aside>

      {/* ----- Right: matrix (tight padding, scroll-only-here) ----- */}
      <main className="flex min-h-0 flex-col overflow-hidden">
        {!selectedUserId || !snapshot.data || !registry.data ? (
          <div className="p-6 text-sm text-zinc-500">
            {selectedUserId
              ? snapshot.isLoading || registry.isLoading
                ? 'Loading…'
                : 'Could not load the matrix.'
              : 'Select a user from the left to manage their per-user permissions.'}
          </div>
        ) : (
          <>
            <header className="flex items-start justify-between gap-3 border-b border-zinc-200 px-4 py-3">
              <div className="min-w-0">
                <h2 className="truncate text-sm font-semibold text-zinc-900">
                  {snapshot.data.userPermissions.length === 0
                    ? 'No extra grants - this user inherits from their role(s).'
                    : `Extra grants: ${snapshot.data.userPermissions.length}`}
                </h2>
                <p className="mt-0.5 text-[11px] text-zinc-500">
                  Locked cells = role-granted (edit the role to revoke).
                  {!targetIsAdmin && (
                    <>
                      {' '}
                      <span className="text-amber-700">
                        User module is admin-only - target user must have role ADMIN to receive
                        user.* grants.
                      </span>
                    </>
                  )}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <UzButton
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  disabled={!dirty || save.isPending}
                >
                  Reset
                </UzButton>
                <UzButton size="sm" onClick={handleSave} loading={save.isPending} disabled={!dirty}>
                  Save
                </UzButton>
              </div>
            </header>
            <div className="flex-1 overflow-auto p-4">
              <PermissionMatrixGrid
                modules={registry.data.modules}
                roleCodes={roleCodes}
                pendingExtras={pendingExtras}
                onToggle={toggleCell}
                targetIsAdmin={targetIsAdmin}
              />
              <MarketingFieldVisibilitySection userId={selectedUserId} />
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// ----- MarketingFieldVisibilitySection -----------------------------------
//
// Per-user, screen-specific "hide these metrics" control for the Marketing
// Reports screen ONLY. Checked = the user CAN see the field; unchecked = hidden
// (stored in the user's hidden list). Enforcement is server-side - the API
// nulls hidden keys in every Marketing Reports response for this user - and the
// screen also skips the matching tile/column so no empty cell shows.

const MarketingFieldVisibilitySection = ({ userId }: { userId: string }): React.JSX.Element => {
  const vis = useUserMarketingFieldVisibility(userId);
  const save = useSetUserMarketingFieldVisibility();

  // Local pending HIDDEN set, hydrated from the server on load / after save.
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (vis.data) setHidden(new Set(vis.data));
  }, [vis.data]);

  const dirty = useMemo(() => {
    const base = new Set(vis.data ?? []);
    if (base.size !== hidden.size) return true;
    for (const k of base) if (!hidden.has(k)) return true;
    return false;
  }, [vis.data, hidden]);

  const toggleVisible = (key: string): void => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setHidden(next);
  };

  const groups = useMemo(
    () =>
      MARKETING_FIELD_GROUP_ORDER.map((group: MarketingFieldGroup) => ({
        group,
        fields: MARKETING_FIELD_CATALOG.filter((f) => f.group === group),
      })),
    [],
  );

  return (
    <section className="mt-8 border-t border-zinc-200 pt-5">
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-zinc-900">
            Marketing Reports - field visibility
          </h3>
          <p className="mt-0.5 max-w-prose text-[11px] text-zinc-500">
            Unchecked fields are hidden from this user on the Marketing Reports screen (tiles and
            table columns), enforced server-side. Applies to this screen only.{' '}
            {hidden.size > 0 ? (
              <span className="text-amber-700">{hidden.size} hidden.</span>
            ) : (
              'Nothing hidden - sees everything their access allows.'
            )}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <UzButton
            variant="ghost"
            size="sm"
            onClick={() => setHidden(new Set(vis.data ?? []))}
            disabled={!dirty || save.isPending}
          >
            Reset
          </UzButton>
          <UzButton
            size="sm"
            onClick={() => save.mutate({ id: userId, hidden: Array.from(hidden) })}
            loading={save.isPending}
            disabled={!dirty}
          >
            Save
          </UzButton>
        </div>
      </header>

      {vis.isLoading ? (
        <div className="mt-3 text-xs text-zinc-500">Loading…</div>
      ) : (
        <div className="mt-4 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2 xl:grid-cols-3">
          {groups.map(({ group, fields }) => (
            <div key={group}>
              <h4 className="mb-2 text-[10px] font-semibold tracking-wide text-zinc-500 uppercase">
                {group}
              </h4>
              <ul className="flex flex-col gap-1.5">
                {fields.map((f) => {
                  const visible = !hidden.has(f.key);
                  return (
                    <li key={f.key}>
                      <label className="flex cursor-pointer items-start gap-2">
                        <input
                          type="checkbox"
                          className="mt-0.5 h-4 w-4 cursor-pointer rounded border-zinc-300 text-indigo-600"
                          checked={visible}
                          onChange={() => toggleVisible(f.key)}
                        />
                        <span className="min-w-0">
                          <span className="block text-[13px] text-zinc-800">{f.label}</span>
                          {f.hint && (
                            <span className="block text-[10px] text-zinc-400">{f.hint}</span>
                          )}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

// ----- PermissionMatrixGrid ----------------------------------------------
//
// Rows = modules from the registry. Columns = the union of actions
// across every visible module so the table reads aligned. A cell is
// blank when its module doesn't expose that action; checked-and-locked
// when the role baseline grants it; togglable otherwise.

interface PermissionMatrixGridProps {
  modules: ReadonlyArray<{ moduleKey: string; label: string; actions: ReadonlyArray<string> }>;
  roleCodes: Set<string>;
  pendingExtras: Set<string>;
  onToggle: (code: string) => void;
  /// When false, every cell in the `user` module is locked off (the
  /// admin-only policy). The cell click handler is also guarded.
  targetIsAdmin: boolean;
}

const PermissionMatrixGrid = ({
  modules,
  roleCodes,
  pendingExtras,
  onToggle,
  targetIsAdmin,
}: PermissionMatrixGridProps): React.JSX.Element => {
  const allActions = useMemo(() => {
    const set = new Set<string>();
    for (const m of modules) for (const a of m.actions) set.add(a);
    return Array.from(set).sort();
  }, [modules]);

  return (
    <table className="min-w-full border-separate border-spacing-0 text-xs">
      <thead>
        <tr>
          <th className="sticky left-0 z-10 bg-white px-2 py-1.5 text-left text-[10px] font-semibold tracking-wide text-zinc-600 uppercase">
            Module
          </th>
          {allActions.map((a) => (
            <th
              key={a}
              className="px-2 py-1.5 text-center text-[10px] font-semibold tracking-wide text-zinc-600 uppercase"
            >
              {a}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {modules.map((m) => {
          // Policy: the `user` module is admin-only. If the selected
          // user is not an ADMIN, every action in this row is locked
          // off (cell click is no-op + visual lock).
          // 2026-06-26 ADR-0056: module key migrated from "user" to
          // "sys.users". The legacy code still seeds the row but the
          // canonical key is the granular one.
          const moduleLocked =
            (m.moduleKey === 'sys.users' || m.moduleKey === 'user') && !targetIsAdmin;
          return (
            <tr
              key={m.moduleKey}
              className={'border-t border-zinc-100 ' + (moduleLocked ? 'bg-amber-50/40' : '')}
            >
              <td className="sticky left-0 z-10 bg-inherit px-2 py-1.5">
                <div className="flex flex-col">
                  <span className="text-[13px] font-medium text-zinc-900">{m.label}</span>
                  <span className="font-mono text-[10px] text-zinc-500">
                    {m.moduleKey}
                    {moduleLocked && (
                      <span className="ml-1 rounded bg-amber-100 px-1 text-[9px] tracking-wide text-amber-700 uppercase">
                        admin only
                      </span>
                    )}
                  </span>
                </div>
              </td>
              {allActions.map((a) => {
                const supports = m.actions.includes(a);
                if (!supports) {
                  return (
                    <td key={a} className="px-2 py-1.5 text-center align-middle">
                      <span className="text-zinc-300">-</span>
                    </td>
                  );
                }
                const code = `${m.moduleKey}.${a}`;
                const grantedByRole = roleCodes.has(code);
                const checked = grantedByRole || pendingExtras.has(code);
                const cellLocked = grantedByRole || moduleLocked;
                return (
                  <td key={a} className="px-2 py-1.5 text-center align-middle">
                    {/* `relative` anchors the sr-only (absolutely positioned)
                        input to this 24px cell. Without it the input's
                        containing block was the page itself: every cell's
                        input stretched the document far past the viewport,
                        and clicking a cell focused an input that sat outside
                        the scroll box, so the browser scrolled the WHOLE app
                        shell up - header and sidebar gone, blank page below. */}
                    <label
                      className={
                        'relative inline-flex h-6 w-6 items-center justify-center rounded-md ring-1 transition-colors ring-inset ' +
                        (cellLocked
                          ? 'cursor-not-allowed bg-zinc-100 ring-zinc-200'
                          : checked
                            ? 'cursor-pointer bg-indigo-50 ring-indigo-300 hover:bg-indigo-100'
                            : 'cursor-pointer bg-white ring-zinc-200 hover:bg-zinc-50')
                      }
                      title={
                        grantedByRole
                          ? 'Granted by role - edit the role to revoke'
                          : moduleLocked
                            ? 'User module is admin-only. Target user must have role ADMIN.'
                            : checked
                              ? 'Extra grant (user-specific)'
                              : 'Not granted'
                      }
                    >
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        disabled={cellLocked}
                        onChange={() => onToggle(code)}
                      />
                      {checked ? (
                        <svg
                          className={
                            grantedByRole
                              ? 'h-3.5 w-3.5 text-zinc-500'
                              : 'h-3.5 w-3.5 text-indigo-600'
                          }
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.7 5.3a1 1 0 0 1 .04 1.4l-7.5 8a1 1 0 0 1-1.45.02L3.3 10.34a1 1 0 1 1 1.4-1.43l3.78 3.78 6.79-7.24a1 1 0 0 1 1.42-.05Z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : null}
                    </label>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
